import { HttpClient } from '@angular/common/http';
import { ElementRef, Input } from '@angular/core';
import {
  Control,
  control,
  CRS,
  Draw,
  FeatureGroup,
  LatLngBounds,
  LayerGroup,
  LeafletEvent,
  LeafletMouseEvent,
  Map as LeafletMap,
  TileLayer,
} from 'leaflet';
import * as L from 'leaflet';
import 'leaflet-draw';
import { fromEvent, Observable, Subject, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { CircleMarker } from './circle-marker';
import { SharedData } from './core/shared-data';
import { LayerFilterCategoriesEnum } from './enums/layer-filter-categories.enum';
import { FILTERCONTROL } from './filters-control';
import { BaseLayer } from './interfaces/base-layer';
import { FeatureInfoParamsSettings } from './interfaces/feature-info-params-settings';
import { LayerFilter } from './interfaces/layer-filter';
import { LayerFilterCategory } from './interfaces/layer-filter-category';
import { MapOptions } from './interfaces/map-options';
import { OverlayLayer } from './interfaces/overlay-layer';
import { PlatformHits } from './interfaces/platform-hits';
import { WMSTileLayer } from './wms-tile-layer';
import { environment } from '../../../frontend/src/environments/environment'
import { FilterService } from 'projects/frontend/src/app/services/filter.service';

const BASE_OPTIONS: MapOptions = {
  center: [0, 0],
  minZoom: 1.5,
  maxZoom: 8,
  zoom:1.5,
  platformInfo: LayerFilterCategoriesEnum.platform_type,
  featureInfoParamsSettings: {
    wmsVersion: 1.1,
    request: 'GetFeatureInfo',
    INFO_FORMAT: 'application/json',
    service: 'WMS',
    version: '1.1.1',
    format: 'image/png',
    transparent: true,
    tiled: true,
    create_bbox: (wmsVersion: number, bounds: LatLngBounds, crs?: CRS) => {
      const nw = crs?.project(bounds.getNorthWest());
      const se = crs?.project(bounds.getSouthEast());
      if (wmsVersion >= 1.3 && crs === CRS.EPSG4326) {
        return [se?.y, nw?.x, nw?.y, se?.x].join(',');
      } else {
        return [nw?.x, se?.y, se?.x, nw?.y].join(',');
      }
    },
    create_projection_key: (wmsVersion: number) => {
      return wmsVersion >= 1.3 ? 'crs' : 'srs';
    },
  },
};

const BASE_LAYERS_PANE_NAME = 'baseLayersPane';
const OVERLAY_LAYERS_PANE_NAME = 'overlayLayersPane';
export class Map extends LeafletMap {
  selectedLayer: string = environment.defaultLayer ? environment.defaultLayer : 'EmodnetPlatformsMetadataV2024_internal'
  rectangleShowed: boolean = false

  wmsLayerTile?: WMSTileLayer;

  private baseLayersPaneName = BASE_LAYERS_PANE_NAME;
  private overlayLayersPaneName = OVERLAY_LAYERS_PANE_NAME;
  public overlayLayersGroup: LayerGroup = new LayerGroup([], {
    pane: OVERLAY_LAYERS_PANE_NAME,
  });
  private overlayLayersGroupBathimetry: LayerGroup = new LayerGroup([], {
    pane: OVERLAY_LAYERS_PANE_NAME,
  });
  private overlayLayersGroupPolygon: LayerGroup = new LayerGroup([], {
    pane: OVERLAY_LAYERS_PANE_NAME,
  });
  public rectangleLayerGroup: LayerGroup = new LayerGroup([], {
    pane: OVERLAY_LAYERS_PANE_NAME,
  });
  public markerLayerGroup: LayerGroup = new LayerGroup([], {
    pane: OVERLAY_LAYERS_PANE_NAME,
  });
  private marker?: CircleMarker;
  private drawnItems = new FeatureGroup();
  extOptions: MapOptions = BASE_OPTIONS;
  baseLayers: TileLayer[] = [];
  public overlayLayers: any = [];
  //private preparedLayerFilterCategories: LayerFilterCategory[] = [];
  layerFilterCategories: LayerFilterCategory[] = [];
  click: Observable<LeafletMouseEvent> = fromEvent<LeafletMouseEvent>(
    this,
    'click'
  );
  boundsChange: Observable<LatLngBounds> = fromEvent<LeafletEvent>(
    this,
    'moveend'
  ).pipe(map((ev) => this.getBounds()));
  bboxChanged: Subject<string> = new Subject<string>();
  filterControlClicked: Subject<boolean> = new Subject<boolean>();
  private overlaylayer:any;
  public radarlayer: LayerGroup = new LayerGroup([], {
    pane: OVERLAY_LAYERS_PANE_NAME,
  });
  private institutionslayer: any;
  public cqlIntstitution: any;
  private platofrmHitsForCql: any[] = [];
  private overlayPlatformLayer: any;
  private currentFilter: any = {
    time: '',
    filter: ''
  };
  private usingRectangle: boolean = false;
  public markerOverlay: any

  constructor(
    element: ElementRef,
    httpClient: HttpClient,
    extOptions?: MapOptions
  ) {
    super(element.nativeElement, BASE_OPTIONS);
    SharedData.getInstance().httpClient = httpClient;
    this.extOptions = { ...BASE_OPTIONS, ...extOptions };
    this.drawnItems.addTo(this);
    /*     this.click = fromEvent<LeafletMouseEvent>(this, 'click');
    this.boundsChange = fromEvent<LeafletEvent>(this, 'moveend').pipe(
      map((ev) => this.getBounds())
    ); */

    const baseLayersPane = this.createPane(this.baseLayersPaneName);
    baseLayersPane.style.zIndex = '' + 100;
    baseLayersPane.style.pointerEvents = 'none';

    const overlayLayersPane = this.createPane(this.overlayLayersPaneName);
    overlayLayersPane.style.zIndex = '' + 200;
    overlayLayersPane.style.pointerEvents = 'none';

    this.overlayLayersGroup.addTo(this);
    this.overlayLayersGroupBathimetry.addTo(this);
    this.rectangleLayerGroup.addTo(this);
    this.markerLayerGroup.addTo(this);
    this.radarlayer.addTo(this);

    let layerToRemove:any;
    this.eachLayer((l: any)=>{if (l._leaflet_id === 32) {
      layerToRemove=l
    }})
    this.removeLayer(layerToRemove);
    // this.overlayLayersGroupPolygon.addTo(this);
    if (this.extOptions?.showZoom) {
      this.zoomControl.setPosition('bottomright');
    }

    let map2 = this;
    const control = new L.Control({ position: 'bottomright' });
    control.onAdd = function () {
      var azoom = L.DomUtil.create('div');
      azoom.innerHTML = `<a class="reset-button" role="button"><svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#000000"><path d="M200-120q-33 0-56.5-23.5T120-200v-160h80v160h160v80H200Zm400 0v-80h160v-160h80v160q0 33-23.5 56.5T760-120H600ZM120-600v-160q0-33 23.5-56.5T200-840h160v80H200v160h-80Zm640 0v-160H600v-80h160q33 0 56.5 23.5T840-760v160h-80Z"/></svg></a>`;
      azoom.classList.add('leaflet-control', 'leaflet-bar');
      L.DomEvent
        .disableClickPropagation(azoom)
        .addListener(azoom, 'click', function () {
          map2.setView([0, 0], 0);
        }, azoom);
      return azoom;
    }
    control.addTo(this);
    if (this.extOptions?.featureInfoParamsSettings) {
      let sub = this.clickSubcribe();

      this.on('draw:created', (event) => {
        const layer = event.layer;
        this.drawnItems.clearLayers()
        this.drawnItems.addLayer(layer);
        this.fitBounds(layer.getBounds());

        this.addFilter({
          name: 'bbox',
          id: 'bbox',
          selected: true,
          iconUrl: './crop.svg',
          menuExcluded: true,
          customFilter: true,
          filters: [
            {
              selected: true,
              layerName: 'platforms',
              filter: `BBOX(geometry,${(layer.getBounds() as LatLngBounds).toBBoxString()}, 'EPSG:4326')`,
            },
          ],
        });
        let platofrmsids = '?platforms='
        this.overlayLayers
          .filter((x:any) => x.wmsLayerOptions?.featureInfo)
          .forEach((x:any) => {
            x.setFeatureInfo(
              this,
              event,
              this.extOptions
                .featureInfoParamsSettings as FeatureInfoParamsSettings,
              'draw'
            )
            x.platformsId.subscribe((ids:any) => {
              for (let index = 0; index < ids.length; index++) {
                const element = ids[index];
                if (ids.length - 1 === index) {
                  platofrmsids = platofrmsids + element;
                } else {
                  platofrmsids = platofrmsids + element + ',';
                }
              }
              const newlink = "https://it-ioos.eu/download-page/" + platofrmsids
              const newanchor = '<a class="download-platform-link" href=' + newlink + '>Download Platforms</a>'
              layer.bindPopup(
                `
                <div class="custom-popup">
                  <p>Click the button below to go to the specific platform page for the selected platforms.</p>
                  <div class="download-platform">
                    ${newanchor}
                  </div>
                </div>
                `,
                { closeOnClick: false }).openPopup();
            })
          });

        //the URL should be updated with the real one
      });

      this.on('draw:toolbaropened', (event) => {
        this.usingRectangle = true;
        sub.unsubscribe();
      });
      this.on('draw:drawstop', (event) => {
        this.rectangleShowed = true;
        const timeout = setTimeout(() => {
          sub = this.clickSubcribe();

          clearTimeout(timeout);
        }, 500);
      });
      this.on('draw:drawstart', (event) => {
        event.layer.featureInfoVisible = false;
        sub.unsubscribe();
      });
    }

    this.on(FILTERCONTROL, (x) => {
      this.filterControlClicked.next(true);
    });

  }

  addFilter(filterCategory: any) {
    this.layerFilterCategories[0].categories = this.layerFilterCategories[0].categories?.slice(0, 5);
    const category = this.layerFilterCategories?.find(
      (x) => x.name == filterCategory.name
    );

    if (category) {
      if (category.customFilter) {
        category.selected = true;
        category.filters?.forEach((x) => {
          x.selected = true;

          filterCategory.filters?.length
            ? (x.filter = filterCategory.filters[0].filter)
            : null;
        });

      } else {
        if (category.exclusive) {
          category.categories?.forEach((x) => {
            x.selected = false;
            x.filters?.forEach((f) => (f.selected = false));
          });
        }

        if (filterCategory.filters) {
          category.filters?.push(...filterCategory.filters);
        }
        if (filterCategory.categories){
          category.categories?.push(...filterCategory.categories);
        }
      }
    } else {
      this.layerFilterCategories?.push(filterCategory);
    }
    if (filterCategory && filterCategory.customFilter) {
      this.updateOverlayLayers();
    } else {
      this.updateOverlayLayers();
    }

  }

  private clickSubcribe(): Subscription {
    return this.click.subscribe((event: any) => {
      this.overlayLayers
        .filter((x:any) => x.wmsLayerOptions?.featureInfo)
        .forEach((x:any) => {

          x.setFeatureInfo(
            this,
            event,
            this.extOptions
              .featureInfoParamsSettings as FeatureInfoParamsSettings,
            'click'
          )
        });

    });
  }

  addBaseLayers(layers: BaseLayer[]) {

    if (layers.length === 1) {
      const tileLayer = new TileLayer(layers[0].url, {
        attribution: layers[0].attribution,
        pane: this.baseLayersPaneName,
      });
      tileLayer.addTo(this);
      this.baseLayers.push(tileLayer);
    } else {
      const layersControl = control
        .layers()
        .setPosition('bottomleft')
      //.addTo(this);
      layers.forEach((layer, i) => {
        const tileLayer = new TileLayer(layer.url, {
          attribution: layer.attribution,
          pane: this.baseLayersPaneName,
        });
        if (i == 0) {
          tileLayer.addTo(this);
        }
        layersControl.addBaseLayer(tileLayer, layer.name);
        this.baseLayers.push(tileLayer);
      });
    }
    // this.addDrawControl();
  }

  addOverlayLayers(
    layer: OverlayLayer,
    layerFilterCategories: LayerFilterCategory[],
    platformHits: PlatformHits[] = []
  ) {
    this.overlayPlatformLayer = layer;
    this.platofrmHitsForCql = platformHits;
    this.layerFilterCategories = layerFilterCategories;

    const preparedLayerFilterCategories = this.prepareLayerFilterCategories(
      this.layerFilterCategories
    );
    this.overlayLayers = []
    this.overlayLayersGroup?.clearLayers();
    this.markerLayerGroup.clearLayers();

    if (this.overlayLayersGroup) {
      const overlayLayer = new WMSTileLayer(layer, this);
      let platformHitsOptions = platformHits.find(
        (x) => x.layerName == layer.layerName
      );

      if (platformHitsOptions) {
        platformHitsOptions.layerFilterCategory = preparedLayerFilterCategories.find(
          (x) => x.id == platformHitsOptions?.layerFilterCategoryId
        );
        platformHitsOptions.layerTypeName = this.selectedLayer
      }

      overlayLayer.platformHitsOptions = platformHitsOptions;
      this.overlayLayers.push(overlayLayer);
      overlayLayer.addTo(this.overlayLayersGroup);
      // this.layerFilterCategories.push(overlayLayer)
    }

  }

  private prepareLayerFilterCategories(
    layerFilterCategories: LayerFilterCategory[]
  ): LayerFilterCategory[] {
    const preparedLayerFilterCategories: LayerFilterCategory[] = [];
    layerFilterCategories.forEach((category) => {
      if (!category) return;
      if (category.id === undefined) {
        let preparedCategory: LayerFilterCategory = {
          id: 'platform_name',
          filters: [],
        };
        category.filters?.forEach((x) => preparedCategory.filters?.push(x));
        preparedLayerFilterCategories.push(preparedCategory);
      }

      let preparedCategory: LayerFilterCategory = {
        id: category?.id,
        filters: [],
      };
      category.filters?.forEach((x) => preparedCategory.filters?.push(x));
      this.prepareFilterCategories(category, preparedCategory.filters ?? []);

      preparedLayerFilterCategories.push(preparedCategory);
    });
    return preparedLayerFilterCategories;
  }

  private prepareFilterCategories(
    category: LayerFilterCategory,
    filters: LayerFilter[]
  ) {
    category.categories?.forEach((x) => {
      x.filters?.forEach((f) => {
        f.iconUrl = x.iconUrl ?? '';
        f.categoryName = x.name ?? '';
        filters.push(f);
      });
      this.prepareFilterCategories(x, filters);
    });
  }

  addDrawControl() {
    const ddrawOptions: Control.DrawConstructorOptions = {
      position: 'bottomright',
      draw: {
        rectangle: <any>{
          shapeOptions: {
            fill: false,
          },
          showArea: false
        },
        circle: false,
        polyline:false,
        polygon: <any>{
          shapeOptions: {
            fill: false,
          },
          showArea: false
        },
        marker: false,
        circlemarker: false,
      },

    };
    const drawControl = new Control.Draw(ddrawOptions);
    this.addControl(drawControl);
    /*
    const newControl = new FilterControl({ position: 'bottomleft' });
    this.addControl(newControl);
    */
  }

  updateOverlayLayers() {
    const categories = this.prepareLayerFilterCategories(
      this.layerFilterCategories
    ).filter((f) => f.filters?.some((s) => s.selected));
    if (!categories.find(s => s.id == 'bbox')?.filters?.some(s => s.selected) ) {
      this.drawnItems.clearLayers();
    }
    if(this.rectangleShowed){
      this.setView([42, 13],6)
      this.rectangleShowed = false;
    }

    this.overlayLayers.forEach((f:any) => {
      if (categories.length == 0 && f.onMap) {
        f.updateFilters('');
        return;
      }
      f.onMap = false;
      f.remove();
    });

    this.overlayLayers
      .filter((x:any) =>
        categories.find((s) =>
          s.filters?.some((ss) => ss.layerName == x.wmsLayerOptions?.layerName)
        )
      )
      .forEach((f:any) => {
        f.onMap = true;
        let filter = categories
          .filter((c) => c.id != 'times' && c.id)
          .map((cat) => {
            const text = cat.filters
              ?.filter(
                (fil) =>
                  fil.selected && f.wmsLayerOptions?.layerName == fil.layerName
              )
              .map((x) => x.filter)
              ?.filter((fil) => fil)
              .join(' OR ');
            return text ? `(${text})` : '';
          })
          .filter((x) => x)
          .join(' AND ');

        const noDataTimeFilter = categories
          .find((c) => c.id == 'times')
          ?.filters?.find((ff) => ff.selected);
        if(noDataTimeFilter?.categoryName=='Custom'){
          filter = filter ? filter + 'AND (' + noDataTimeFilter.value + ')' : filter + ' (' + noDataTimeFilter.value + ')'
        }

        const timeFilter = categories.find((c) => c.id == 'times')?.filters?.find((ff) => ff.selected)?.filter;

        const event = new CustomEvent('filterUpdated', { detail: {filter, timeFilter} });
        document.dispatchEvent(event);
        f.updateFilters(filter, timeFilter);
        f.addTo(this.overlayLayersGroup);
      });
  }


  addBathymetricOverlay(layer: any) {
    if (layer) {
      const overlayLayer = new WMSTileLayer(layer, this);
      overlayLayer.addTo(this.overlayLayersGroupBathimetry);
    }
  }

  updateOverlayMapCqlFilter(cqlFilter: any) {
    // const newCqlFilter = " (1=1) AND (" + cqlFilter + ") OR (projects_code like 'EMSO')"
    // this.overlayPlatformLayer['cql_filter'] = newCqlFilter;
    // this.updateOverlayLayers();
  }


  addPolygonOverlay(ids: any, polygon: any) {
    // if (ids.length > 0 && polygon) {
    //   this.overlayLayersGroupPolygon.clearLayers();
    //   for (const feature of polygon.features) {
    //     if (ids.includes(feature.properties.color)) {
    //       this.institutionslayer = L.polygon(feature.geometry.coordinates, { color: feature.properties.color })
    //       this.institutionslayer.addTo(this.overlayLayersGroupPolygon);
    //     }
    //   }
    // } else {
    //   for (const feature of polygon.features) {
    //     if (feature.properties.color) {
    //       this.institutionslayer = L.polygon(feature.geometry.coordinates, { color: feature.properties.color })
    //       this.institutionslayer.addTo(this.overlayLayersGroupPolygon);
    //     }
    //   }
    // }
  }

  removePolygonOverlay() {
    this.overlayLayersGroupPolygon.clearLayers();
  }

  public addGeoJsonRadar(radars: any[]){

  }

}
